import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { storageGet, storageSet } from './hooks';

export interface CartLine {
  key: string;
  /** custom: a one-off item typed at the counter (not in inventory). deposit: money paid ahead on a repair ticket. */
  kind: 'product' | 'device' | 'repair' | 'billpay' | 'activation' | 'order' | 'custom' | 'deposit';
  id: string;
  name: string;
  detail?: string | null;
  listPriceCents: number;
  unitPriceCents: number;
  qty: number;
  discountCents: number;
  taxable: boolean;
  /** Repairs: the taxed parts and tax-free labor pieces, in the order the server charges them. */
  parts?: { priceCents: number; taxable: boolean }[];
  image?: string | null;
  /** Products from the sale menu: size, color, IMEI or serial, printed on the receipt. */
  note?: string | null;
  billpay?: BillPayDraft;
  activation?: ActivationDraft;
  /** A deposit: the ticket it's for (the amount is unitPriceCents). */
  deposit?: { repairId: string; ticket: string };
  /** A repair: deposits already paid on it, taken off at checkout (the server works them out again). */
  deposits?: { receiptNo: number; amountCents: number }[];
  art?: { kind: 'product'; categoryId: string | null } | { kind: 'device'; brand: string; model: string; color: string | null; categoryId: string | null };
}

export interface BillPayDraft { carrier: string; account: string; amountCents: number; feeCents: number; surchargeCents?: number; confirmation?: string | null }
/** An activation in the sale. What the carrier pays the store isn't here: the owner's rates decide it on the server. */
export interface ActivationDraft { carrier: string; type: string; phone?: string | null; plan?: string | null; sim?: string | null; imei?: string | null; confirmation?: string | null; feeCents: number }

/**
 * Lines as the tax math sees them: a repair counts as its parts and labor pieces; a bill payment as
 * the payment, surcharge and fee. Deposits aren't in it: they're at face value (see faceValueCents).
 */
export function calcLines(lines: CartLine[]) {
  const out: { qty: number; unitPriceCents: number; discountCents: number; taxable: boolean; owner: number }[] = [];
  lines.forEach((l, i) => {
    if (l.kind === 'deposit') return;
    if (l.kind === 'repair' && l.parts?.length) {
      for (const p of l.parts) out.push({ qty: 1, unitPriceCents: p.priceCents, discountCents: 0, taxable: p.taxable, owner: i });
    } else if (l.kind === 'billpay' && l.billpay) {
      for (const c of [l.billpay.amountCents, l.billpay.surchargeCents ?? 0, l.billpay.feeCents]) if (c) out.push({ qty: 1, unitPriceCents: c, discountCents: 0, taxable: false, owner: i });
    } else {
      out.push({ qty: l.qty, unitPriceCents: l.unitPriceCents, discountCents: l.discountCents, taxable: l.taxable, owner: i });
    }
  });
  return out;
}

/** What deposits add (one being taken) and take off (ones paid on a repair here), outside discounts and tax. */
export function faceValueCents(lines: CartLine[]): number {
  return lines.reduce((s, l) => s + (l.kind === 'deposit' ? l.unitPriceCents : 0) - (l.kind === 'repair' ? (l.deposits ?? []).reduce((d, x) => d + x.amountCents, 0) : 0), 0);
}

export interface CartCustomer {
  id: string;
  name: string;
  phone: string | null;
  email?: string | null;
  store_credit_cents: number;
  points_balance?: number;
  member_code?: string | null;
}

/** A trade-in taken for this sale: its credit comes off the phone, and it prints on the receipt. */
export interface TradeInRef { id: string; number: string; device: string; imei: string | null; credit_cents: number }

export interface Cart {
  lines: CartLine[];
  customer: CartCustomer | null;
  cartDiscountCents: number;
  tradeIns?: TradeInRef[];
  /** "Remove taxes": no sales tax on this sale, and why (a manager approves at checkout). */
  taxRemoved?: { reason: string } | null;
  /** Made at the first try to pay for this cart, kept until it's cleared (see checkoutRequestId). */
  checkoutSeed?: string | null;
}

const EMPTY: Cart = { lines: [], customer: null, cartDiscountCents: 0, tradeIns: [] };
/** The last item taken out: the tax comes back for whatever is rung up next. */
const emptied = (c: Cart): Cart => (c.lines.length || !c.taxRemoved ? c : { ...c, taxRemoved: null });

interface CartApi {
  cart: Cart;
  /**
   * Adds (or bumps the quantity of) a product. Returns the cart line's key. With a note (a phone's
   * color or IMEI) or a price for this sale only, it always gets its own line.
   */
  addProduct(p: { id: string; name: string; sku?: string | null; price_cents: number; taxable: boolean; image_url?: string | null; category_id?: string | null }, qty?: number, opts?: { note?: string | null; priceCents?: number }): string;
  /** Adds a phone. Returns the cart line's key, or null if it's already in the cart. */
  addDevice(d: { id: string; title: string; detail: string; price_cents: number; taxable: boolean; brand?: string; model?: string; color?: string | null; category_id?: string | null }): string | null;
  /** Adds a repair ticket at its prices, with any deposits paid on it. Returns the key, or null if it's already in the cart. */
  addRepair(r: {
    id: string; ticket: string; brand: string; model: string; color: string | null; lines: { description: string }[]; checkout_parts: { priceCents: number; taxable: boolean }[]; totals: { subtotal_cents: number };
    deposits?: { list: { receipt_no: number; amount_cents: number; refunded: boolean; used_on: unknown }[] };
  }): string | null;
  /** A one-off item typed at the counter (needs a manager's OK for staff who can't change prices). */
  addCustom(c: { description: string; priceCents: number; taxable: boolean }): string;
  /** A deposit on a repair ticket. One per ticket: a new amount replaces the one in the cart. */
  addDeposit(d: { repairId: string; ticket: string; device: string; amountCents: number }): string;
  /** Adds a carrier bill payment (the payment, plus the store's fee). */
  addBillPay(b: BillPayDraft): string;
  /** Adds a phone activation fee. */
  addActivation(a: ActivationDraft): string;
  /** Adds a special order being picked up. Returns null if it's already in the cart. */
  addOrder(o: { id: string; number: string; description: string; price_cents: number; taxable: boolean }): string | null;
  setQty(key: string, qty: number): void;
  updateLine(key: string, patch: Partial<Pick<CartLine, 'unitPriceCents' | 'listPriceCents' | 'discountCents'>>): void;
  remove(key: string): void;
  setCustomer(c: CartCustomer | null): void;
  /** A trade-in for this sale (its customer is the sale's customer). */
  addTradeIn(t: TradeInRef): void;
  removeTradeIn(id: string): void;
  setCartDiscount(cents: number): void;
  /** Takes the tax off the sale (with why), or puts it back (null). */
  setTaxRemoved(reason: string | null): void;
  clear(): void;
  /** Puts a whole cart back (a parked sale). */
  replace(c: Cart): void;
  /** This cart's checkout seed, made the first time it's asked for. */
  checkoutSeed(): string;
}

const Ctx = createContext<CartApi>(null as unknown as CartApi);
const KEY = 'storeos.cart';

export function CartProvider({ children, scope }: { children: ReactNode; scope: string }) {
  const storageKey = `${KEY}.${scope}`;
  const [cart, setCart] = useState<Cart>(() => storageGet<Cart>(storageKey, EMPTY));
  useEffect(() => storageSet(storageKey, cart.lines.length || cart.customer || cart.tradeIns?.length ? cart : null), [cart, storageKey]);

  // Mirrors the latest cart so add functions can answer synchronously.
  const cartRef = useRef(cart);
  cartRef.current = cart;

  const addProduct: CartApi['addProduct'] = useCallback((p, qty = 1, opts = {}) => {
    const note = opts.note?.trim() || null;
    const own = !!note || (opts.priceCents !== undefined && opts.priceCents !== p.price_cents);
    const same = (l: CartLine) => l.kind === 'product' && l.id === p.id && !l.note && l.unitPriceCents === l.listPriceCents;
    const found = own ? undefined : cartRef.current.lines.find(same);
    const key = found?.key ?? `p-${p.id}-${Date.now()}`;
    setCart((c) => {
      const existing = own ? undefined : c.lines.find(same);
      if (existing) return { ...c, lines: c.lines.map((l) => (l === existing ? { ...l, qty: l.qty + qty } : l)) };
      const detail = [p.sku ? `SKU ${p.sku}` : null, note].filter(Boolean).join(' · ') || null;
      const price = opts.priceCents ?? p.price_cents;
      return { ...c, lines: [...c.lines, { key, kind: 'product', id: p.id, name: p.name, detail, note, listPriceCents: p.price_cents, unitPriceCents: price, qty, discountCents: 0, taxable: p.taxable, image: p.image_url ?? null, art: { kind: 'product', categoryId: p.category_id ?? null } }] };
    });
    return key;
  }, []);

  const addDevice: CartApi['addDevice'] = useCallback((d) => {
    if (cartRef.current.lines.some((l) => l.kind === 'device' && l.id === d.id)) return null;
    const key = `d-${d.id}`;
    setCart((c) => {
      if (c.lines.some((l) => l.kind === 'device' && l.id === d.id)) return c;
      return { ...c, lines: [...c.lines, { key, kind: 'device', id: d.id, name: d.title, detail: d.detail, listPriceCents: d.price_cents, unitPriceCents: d.price_cents, qty: 1, discountCents: 0, taxable: d.taxable, art: { kind: 'device', brand: d.brand ?? '', model: d.model ?? '', color: d.color ?? null, categoryId: d.category_id ?? null } }] };
    });
    return key;
  }, []);

  const addRepair: CartApi['addRepair'] = useCallback((r) => {
    if (cartRef.current.lines.some((l) => l.kind === 'repair' && l.id === r.id)) return null;
    const key = `r-${r.id}`;
    setCart((c) => {
      if (c.lines.some((l) => l.kind === 'repair' && l.id === r.id)) return c;
      const line: CartLine = {
        key, kind: 'repair', id: r.id, name: `Repair ${r.ticket}`, detail: `${r.brand} ${r.model} · ${r.lines.map((x) => x.description).join(', ')}`,
        listPriceCents: r.totals.subtotal_cents, unitPriceCents: r.totals.subtotal_cents, qty: 1, discountCents: 0, taxable: r.checkout_parts.some((p) => p.taxable),
        parts: r.checkout_parts.map((p) => ({ priceCents: p.priceCents, taxable: p.taxable })),
        deposits: (r.deposits?.list ?? []).filter((d) => !d.refunded && !d.used_on).map((d) => ({ receiptNo: d.receipt_no, amountCents: d.amount_cents })),
        art: { kind: 'device', brand: r.brand, model: r.model, color: r.color, categoryId: null },
      };
      // Paying for the whole repair now: a deposit on it that was about to be taken isn't needed.
      return { ...c, lines: [...c.lines.filter((l) => !(l.kind === 'deposit' && l.deposit?.repairId === r.id)), line] };
    });
    return key;
  }, []);

  const addBillPay: CartApi['addBillPay'] = useCallback((b) => {
    const key = `b-${Date.now()}`;
    const total = b.amountCents + (b.surchargeCents ?? 0) + b.feeCents;
    setCart((c) => ({ ...c, lines: [...c.lines, { key, kind: 'billpay', id: key, name: `Bill payment · ${b.carrier}`, detail: `Account …${b.account.replace(/\D/g, '').slice(-4)}${b.confirmation ? ` · Conf ${b.confirmation}` : ''} · fee ${(b.feeCents / 100).toFixed(2)}`, listPriceCents: total, unitPriceCents: total, qty: 1, discountCents: 0, taxable: false, billpay: b }] }));
    return key;
  }, []);
  const addActivation: CartApi['addActivation'] = useCallback((a) => {
    const key = `a-${Date.now()}`;
    setCart((c) => ({ ...c, lines: [...c.lines, { key, kind: 'activation', id: key, name: `Activation · ${a.carrier}`, detail: [a.phone, a.plan].filter(Boolean).join(' · ') || null, listPriceCents: a.feeCents, unitPriceCents: a.feeCents, qty: 1, discountCents: 0, taxable: false, activation: a }] }));
    return key;
  }, []);
  const addCustom: CartApi['addCustom'] = useCallback((c) => {
    const key = `c-${Date.now()}`;
    setCart((x) => ({ ...x, lines: [...x.lines, { key, kind: 'custom', id: key, name: c.description, detail: c.taxable ? 'Custom item' : 'Custom item · no tax', listPriceCents: c.priceCents, unitPriceCents: c.priceCents, qty: 1, discountCents: 0, taxable: c.taxable }] }));
    return key;
  }, []);
  const addDeposit: CartApi['addDeposit'] = useCallback((d) => {
    const key = `dep-${d.repairId}`;
    const line: CartLine = { key, kind: 'deposit', id: d.repairId, name: `Deposit on ${d.ticket}`, detail: `${d.device} · goes toward the repair`, listPriceCents: d.amountCents, unitPriceCents: d.amountCents, qty: 1, discountCents: 0, taxable: false, deposit: { repairId: d.repairId, ticket: d.ticket } };
    setCart((x) => {
      // A deposit instead of paying for the whole repair now.
      const lines = x.lines.filter((l) => !(l.kind === 'repair' && l.id === d.repairId));
      return lines.some((l) => l.key === key) ? { ...x, lines: lines.map((l) => (l.key === key ? line : l)) } : { ...x, lines: [...lines, line] };
    });
    return key;
  }, []);
  const addOrder: CartApi['addOrder'] = useCallback((o) => {
    if (cartRef.current.lines.some((l) => l.kind === 'order' && l.id === o.id)) return null;
    const key = `o-${o.id}`;
    setCart((c) => (c.lines.some((l) => l.kind === 'order' && l.id === o.id) ? c : { ...c, lines: [...c.lines, { key, kind: 'order', id: o.id, name: `Special order ${o.number}`, detail: o.description, listPriceCents: o.price_cents, unitPriceCents: o.price_cents, qty: 1, discountCents: 0, taxable: o.taxable }] }));
    return key;
  }, []);

  const value: CartApi = {
    cart,
    addProduct,
    addDevice,
    addRepair,
    addBillPay,
    addActivation,
    addOrder,
    addCustom,
    addDeposit,
    setQty: (key, qty) => setCart((c) => emptied({ ...c, lines: qty <= 0 ? c.lines.filter((l) => l.key !== key) : c.lines.map((l) => (l.key === key ? { ...l, qty } : l)) })),
    updateLine: (key, patch) => setCart((c) => ({ ...c, lines: c.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) })),
    remove: (key) => setCart((c) => emptied({ ...c, lines: c.lines.filter((l) => l.key !== key) })),
    // A different customer: trade-ins taken from the last one don't come along.
    setCustomer: (customer) => setCart((c) => ({ ...c, customer, tradeIns: customer && customer.id === c.customer?.id ? c.tradeIns ?? [] : [] })),
    addTradeIn: (t) => setCart((c) => ({ ...c, tradeIns: [...(c.tradeIns ?? []).filter((x) => x.id !== t.id), t] })),
    removeTradeIn: (id) => setCart((c) => ({ ...c, tradeIns: (c.tradeIns ?? []).filter((x) => x.id !== id) })),
    setCartDiscount: (cents) => setCart((c) => ({ ...c, cartDiscountCents: Math.max(0, cents) })),
    setTaxRemoved: (reason) => setCart((c) => ({ ...c, taxRemoved: reason?.trim() ? { reason: reason.trim() } : null })),
    clear: () => setCart(EMPTY),
    replace: (c) => setCart({ ...EMPTY, ...c, lines: Array.isArray(c.lines) ? c.lines : [], checkoutSeed: null }),
    checkoutSeed: () => {
      const have = cartRef.current.checkoutSeed;
      if (have) return have;
      const seed = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      cartRef.current = { ...cartRef.current, checkoutSeed: seed };
      setCart((c) => ({ ...c, checkoutSeed: c.checkoutSeed || seed }));
      return seed;
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart() {
  return useContext(Ctx);
}

/**
 * The id a checkout is sent with: the cart's seed and what's in it. Trying again after "No
 * connection" (or closing Payment and paying again) sends the same id for the same cart, and the
 * server gives back the sale already saved instead of ringing it up twice. A cleared cart gets a
 * new seed, so the next customer's identical sale is a new sale.
 */
export function checkoutRequestId(seed: string, req: ReturnType<typeof cartToRequest>): string {
  const text = JSON.stringify([req.lines, req.customerId, req.cartDiscountCents ?? 0, req.removeTax ?? null]);
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193) >>> 0;
  return `${seed.slice(0, 40)}-${h.toString(36)}`;
}

/** What the server needs for a sale or a quote. */
export function cartToRequest(cart: Cart) {
  return {
    lines: cart.lines.map((l) =>
      l.kind === 'repair'
        ? { repairId: l.id }
        : l.kind === 'billpay'
        ? { billpay: l.billpay }
        : l.kind === 'activation'
        ? { activation: l.activation }
        : l.kind === 'order'
        ? { orderId: l.id }
        : l.kind === 'deposit'
        ? { deposit: { repairId: l.deposit?.repairId ?? l.id, amountCents: l.unitPriceCents } }
        : l.kind === 'custom'
        ? { custom: { description: l.name, priceCents: l.unitPriceCents, taxable: l.taxable }, qty: l.qty, ...(l.discountCents ? { discountCents: l.discountCents } : {}) }
        : {
            ...(l.kind === 'product' ? { productId: l.id, qty: l.qty, ...(l.note ? { note: l.note } : {}) } : { deviceId: l.id }),
            ...(l.unitPriceCents !== l.listPriceCents ? { priceCents: l.unitPriceCents } : {}),
            ...(l.discountCents ? { discountCents: l.discountCents } : {}),
          },
    ),
    cartDiscountCents: cart.cartDiscountCents || undefined,
    customerId: cart.customer?.id ?? null,
    ...(cart.taxRemoved ? { removeTax: { reason: cart.taxRemoved.reason } } : {}),
  };
}
